import { t } from "./t";
import { englishDictionary } from "./store";
import type { PlainKey } from "./types";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-85 S5 — category, flower type and colour names in the florist's language
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * These are DATABASE ROWS, seeded in English by `prisma/seed-reference.ts`
 * and `COLOR_PALETTE` in `domain-enums.ts`, and rendered straight onto the
 * kiosk: the builder's tab strip and type chips, the item finder, the waste
 * flow, the item card, photo search. Until now a fully Russian screen still
 * said "Carnations" and "Ivory", because no amount of extraction reaches a
 * string that lives in Postgres.
 *
 * The owner asked for these by name when the CRM order was set (2026-09-19):
 * category, flower type and colour names are IN scope.
 *
 * ── Why this is not just `t()` ─────────────────────────────────────────────
 *
 * `translate()` ends its resolution chain by returning THE KEY, deliberately:
 * a missing developer-authored string should be visible as a bug. That is
 * exactly wrong here. The owner can seed a new flower type at any time, and
 * the florist would read `taxonomy.flowerType.PROTEA` on the chip. So these
 * helpers check the English dictionary for the key FIRST and fall back to the
 * database's own label — English, but a real word — when it is absent.
 *
 * Checking `englishDictionary()` and not the active one is on purpose: en.json
 * is the source of truth, it is always loaded, and `dictionaries.spec` already
 * guarantees the other four carry an identical key set. A key present in
 * English is present everywhere.
 *
 * ── The cost, accepted and recorded ────────────────────────────────────────
 *
 * A type or colour added after this ships shows in whatever language it was
 * typed in until someone adds a key. `taxonomy.spec` fails the build when a
 * SEEDED code has no key, so the reference list cannot drift quietly; a row
 * created at runtime in the app is outside what a test can see.
 *
 * ── Keys keep the code verbatim ────────────────────────────────────────────
 *
 * `taxonomy.flowerType.RICE_FLOWER`, not `…riceFlower` — S3's rule, so the key
 * and the database value it maps cannot drift apart. Colours are the exception
 * and cannot follow it: `Color` has no code column, only a unique `name`, so
 * the key is derived from the name and a rename in the database orphans it.
 * That is a real fragility and it is the schema's, not this file's.
 */

function labelFrom(
  prefix: string,
  code: string | null | undefined,
  dbLabel: string | null | undefined,
): string {
  if (code) {
    const key = `${prefix}.${code}`;
    // The cast is safe BECAUSE of the guard above it: the key is only used
    // when en.json actually carries it, which is what makes it a PlainKey.
    if (englishDictionary()[key] !== undefined) return t(key as PlainKey);
  }
  return dbLabel ?? code ?? "";
}

/** `FLOWER` → "Flower" / "Flor" / "Цветок". */
export function categoryLabel(
  code: string | null | undefined,
  dbLabel?: string | null,
): string {
  return labelFrom("taxonomy.category", code, dbLabel);
}

/** `ROSE` → "Roses" / "Rosas" / "Розы". */
export function flowerTypeLabel(
  code: string | null | undefined,
  dbLabel?: string | null,
): string {
  return labelFrom("taxonomy.flowerType", code, dbLabel);
}

/**
 * "Ivory" → "Marfil" / "Слоновая кость".
 *
 * Keyed on the NAME because the schema gives colours nothing else. The name
 * is uppercased and its spaces become underscores so the key looks like every
 * other one here; `Color.name` is `citext`, so case cannot be relied on.
 */
export function colourKeyFor(name: string): string {
  return name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_");
}

export function colourLabel(name: string | null | undefined): string {
  if (!name) return "";
  return labelFrom("taxonomy.colour", colourKeyFor(name), name);
}
